/* -----------------Globals--------------- */
import { isAndroid, isIOS, isWeb } from "@memorang/helpers/platform";
import { useMemo, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import * as Clipboard from "expo-clipboard";

/* -----------------Child components--------------- */
import { FeedbackDialog } from "@memorang/ui/components/FeedbackDialog";

import { useTranslation } from "react-i18next";
import type { FeedbackParams } from "../../feedback";
/* -----------------Types--------------- */
import type { HandleTracking } from "../../types";

import { getFeedbackReasonsByType } from "@memorang/helpers/feedback";
import type { UnifiedCitation } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import type { MenuAction } from "@memorang/ui/components/ChatUI/types";
import CustomSnackbar from "@memorang/ui/components/CustomSnackbar";
import SourceList from "@memorang/ui/components/SourceList";
import { useToast } from "@memorang/ui/components/Toast";
import type { IconType } from "@memorang/ui/icon";
import { useDevice } from "@memorang/ui/useDevice";
import { router } from "expo-router";
import { useSendFeedback } from "../../feedback/hooks/useSendFeedback";
/* -----------------Constants--------------- */
import { events } from "../constants";
import { useSendEmail } from "./useSendEmail";
type Props = {
	handleTracking: HandleTracking;
	searchQuery?: string;
	feedbackDialogParams?: FeedbackParams;
	handleNavigateToReference?: (url: string) => void;
};

export const useMessageActions = ({
	handleTracking,
	searchQuery,
	feedbackDialogParams,
	handleNavigateToReference,
}: Props) => {
	const sendFeedback = useSendFeedback();
	const sendEmail = useSendEmail();
	const { showToast } = useToast();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [showCopyFeedback, setShowCopyFeedback] = useState(false);
	const [sources, setSources] = useState<UnifiedCitation[]>([]);
	const [runId, setRunId] = useState<string | undefined>();
	const { isMobile } = useDevice();
	const [showSourcesDialog, setShowSourcesDialog] = useState(false);

	const [feedbackRatingType, setFeedbackRatingType] = useState<
		"positive" | "negative" | "reportIssue"
	>();

	const { t } = useTranslation();
	const toggleShowFeedbackDialog = (): void => {
		setShowFeedbackDialog((prev) => !prev);
	};
	const handleShowAllSources = (sources: UnifiedCitation[]) => {
		setShowSourcesDialog(true);
		setSources(sources);
	};
	const handleSendEmail = async (text?: string) => {
		await sendEmail({
			text,
			examName: feedbackDialogParams?.context,
			toEmail: feedbackDialogParams?.email,
			companyName: feedbackDialogParams?.companyName,
		});
		handleTracking(events.emailSent);
	};

	const handleEmailIconPress = async (text?: string) => {
		handleTracking(events.emailTriggered);
		try {
			await handleSendEmail(text);
		} catch (error) {
			showToast({
				message: t("report.sessionAnalysis.menuActions.emailFailed", {
					email: feedbackDialogParams?.email,
				}),
				duration: 3000,
			});
			console.error(error);
		}
	};

	const handleCopyIconPress = async (text?: string) => {
		if (!text) {
			return;
		}

		const textToCopy = searchQuery
			? `# Question:\n${searchQuery}\n\n# Answer:\n${text}`
			: text;

		if (isWeb) {
			navigator.clipboard.writeText(textToCopy);
		} else if (isAndroid || isIOS) {
			await Clipboard.setStringAsync(textToCopy);
		}

		handleTracking(events.copilotResponseCopyClicked, {
			query: searchQuery ?? "",
			summary: text,
		});
		setShowCopyFeedback(true);
	};

	const handlePositiveFeedbackIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("positive", runId);
		} else {
			setFeedbackRatingType("positive");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackPositive);
	};

	const handleNegativeFeedbackIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("negative", runId);
		} else {
			setFeedbackRatingType("negative");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackNegative);
	};

	const handleReportIssueIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("reportIssue", runId);
		} else {
			setFeedbackRatingType("reportIssue");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackReport);
	};

	const handleNavigateToFeedback = (
		feedbackRatingType?: "positive" | "negative" | "reportIssue",
		runId?: string,
	) => {
		const isReportIssue = feedbackRatingType === "reportIssue";
		const isNegative = feedbackRatingType === "negative";
		const isPositive = feedbackRatingType === "positive";

		const getFeedbackTitle = () => {
			if (isReportIssue) {
				return t("dialog.report.title");
			}
			return undefined;
		};

		const getFeedbackSubtitle = () => {
			if (isReportIssue) {
				return t("dialog.report.body");
			}
			if (isNegative) {
				return t("dialog.feedback.negativeBody");
			}
			if (isPositive) {
				return t("dialog.feedback.positiveBody");
			}
			return t("report.sessionAnalysis.feedbackDialog.body");
		};

		const getFeedbackIcon = () => {
			if (isReportIssue) {
				return "flag-outline";
			}
			return undefined;
		};

		const getFeedbackCta = () => {
			if (isReportIssue) {
				return t("dialog.report.cta");
			}
			return undefined;
		};

		router.push({
			pathname: "/feedback",
			params: {
				view: feedbackDialogParams?.view ?? "session",
				featureName: feedbackDialogParams?.feature,
				feedbackTitle: getFeedbackTitle(),
				feedbackSubtitle: getFeedbackSubtitle(),
				feedbackIcon: getFeedbackIcon(),
				itemFeedbackRating:
					feedbackRatingType === "reportIssue"
						? "negative"
						: feedbackRatingType,
				sessionId: feedbackDialogParams?.sessionId,
				runId,
				type:
					feedbackRatingType === "reportIssue" ? "Content Flagged" : undefined,
				placeholder: t("report.sessionAnalysis.feedbackDialog.placeholder"),
				feedbackCta: getFeedbackCta(),
			},
		});
	};

	const messageActions = (
		showEmail?: boolean,
		runId?: string,
	): {
		icon: string;
		altText: string;
		tooltip: string;
		handleOnPress: (text?: string) => void;
	}[] => [
		...(showEmail
			? [
					{
						icon: "email-outline",
						tooltip: t("report.sessionAnalysis.menuActions.emailSummary"),
						altText: "Email",
						handleOnPress: handleEmailIconPress,
					},
				]
			: []),
		{
			icon: "content-copy",
			tooltip: t("report.sessionAnalysis.menuActions.copy"),
			altText: t("report.sessionAnalysis.menuActions.altCopy"),
			handleOnPress: handleCopyIconPress,
		},
		{
			icon: "thumb-up-outline",
			tooltip: t("report.sessionAnalysis.menuActions.positiveFeedback"),
			altText: t("report.sessionAnalysis.menuActions.altPositiveFeedback"),
			handleOnPress: () => handlePositiveFeedbackIconPress(runId),
		},
		{
			icon: "thumb-down-outline",
			tooltip: t("report.sessionAnalysis.menuActions.negativeFeedback"),
			altText: t("report.sessionAnalysis.menuActions.altReportIssue"),
			handleOnPress: () => handleNegativeFeedbackIconPress(runId),
		},
		{
			icon: "flag-outline",
			tooltip: t("report.sessionAnalysis.menuActions.reportIssue"),
			altText: t("report.sessionAnalysis.menuActions.altReportIssue"),
			handleOnPress: () => handleReportIssueIconPress(runId),
		},
	];

	const appBarFeedbackAction: MenuAction = {
		icon: "comment-quote-outline",
		title: t("common.leaveFeedback"),
		handleAction: () => {
			if (isMobile) {
				handleNavigateToFeedback();
			} else {
				setFeedbackRatingType(undefined);
				toggleShowFeedbackDialog();
			}
		},
	};

	const handlePostFeedback = (
		feedback: string,
		email: string,
		selectedReasons?: string[],
	) => {
		handleTracking(events.feedbackSubmitted, {
			type: feedbackRatingType!,
			feedback: selectedReasons?.length
				? `${selectedReasons.join(", ")} \n\n ${feedback}`
				: feedback,
			email,
		});
	};

	const isReportIssue = feedbackRatingType === "reportIssue";
	const isNegative = feedbackRatingType === "negative";
	const isPositive = feedbackRatingType === "positive";

	const getTitle = () => {
		if (isReportIssue) {
			return t("dialog.report.title");
		}
		return undefined;
	};

	const getSubtitle = () => {
		if (isReportIssue) {
			return t("dialog.report.body");
		}
		if (isNegative) {
			return t("dialog.feedback.negativeBody");
		}
		if (isPositive) {
			return t("dialog.feedback.positiveBody");
		}
		return t("report.sessionAnalysis.feedbackDialog.body");
	};

	const getIcon = () => {
		if (isReportIssue) {
			return "flag-outline";
		}
		return "comment-quote-outline";
	};

	const getCta = () => {
		if (isReportIssue) {
			return t("dialog.report.cta");
		}
		return t("report.sessionAnalysis.feedbackDialog.cta");
	};

	const negativeReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("negative", t),
		[t],
	);

	const reportReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("report", t),
		[t],
	);

	const feedbackReasonList = isReportIssue
		? reportReasonList
		: isNegative
			? negativeReasonList
			: undefined;
	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const messageFeedbackComponents = useMemo(
		() => (
			<>
				{showSourcesDialog && (
					<SourceList
						sources={sources}
						onPress={(source) => handleNavigateToReference?.(source.url ?? "")}
						onClose={() => setShowSourcesDialog(false)}
						visible={showSourcesDialog}
					/>
				)}
				{feedbackDialogParams && (
					<FeedbackDialog
						visible={showFeedbackDialog}
						title={getTitle()}
						subtitle={getSubtitle()}
						icon={getIcon()}
						cta={getCta()}
						onDismiss={toggleShowFeedbackDialog}
						handleTracking={handleTracking}
						feedbackDialogParams={feedbackDialogParams}
						cancelCta={t("common.cancel")}
						successMessage={t(
							"report.sessionAnalysis.feedbackDialog.successMessage",
						)}
						feedbackLimitError={t(
							"report.sessionAnalysis.feedbackDialog.feedbackLimitError",
						)}
						feedbackPlaceholder={t(
							"report.sessionAnalysis.feedbackDialog.placeholder",
						)}
						feedbackReasonList={feedbackReasonList}
						handlePostFeedback={handlePostFeedback}
						sendFeedback={sendFeedback}
					/>
				)}
				<Box position="absolute" bottom={54} left={0} right={0}>
					<CustomSnackbar
						visible={showCopyFeedback}
						message={"Copied response to clipboard"}
						duration={1500}
						handleDismiss={() => setShowCopyFeedback(false)}
					/>
				</Box>
			</>
		),
		[
			showFeedbackDialog,
			feedbackDialogParams,
			feedbackRatingType,
			handleTracking,
			runId,
			showCopyFeedback,
			isMobile,
			showSourcesDialog,
			sources,
			handleNavigateToReference,
		],
	);

	return {
		messageActions,

		messageFeedbackComponents,
		appBarFeedbackAction,
		handleShowAllSources,
	};
};
